import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./TracePaywall.module.scss"

/**
 * TracePaywall (`/trace`) — an original, self-contained ONBOARDING paywall for
 * Private Photo Vault Premium. Paywall type rolled at random this run: ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority, and the whole concept is built to it: the app's real
 * Pattern Lock, told as the intimate ritual of setting a secret only you know.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "A secret only you know." Right now nothing stands between your photos and
 * whoever is holding your phone — pick it up and the memories are simply there.
 * Private Photo Vault puts YOUR OWN SECRET in front of every memory: a pattern
 * you trace once, that becomes the only way in. The onboarding truth told as
 * the moment you thread a private shape through your own photos.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * The hero is a 3×3 constellation of your photos with a glowing node at the
 * centre of each. You TRACE YOUR SECRET — drag from photo to photo and a
 * luminous thread stitches the pattern across your memories. The instant the
 * shape is drawn, the photos gather behind a frosted privacy pane, a lock draws
 * shut at the centre, a Face-ID "only you" seal blooms, and the status flips
 * "Open to anyone → Locked with your secret." A TRACE-A-PATH-THROUGH-NODES
 * gesture (connect-the-dots) — new to the set: not a slide-the-tray (Strongbox),
 * a ring-sweep (Aperture), a phone tilt (Facedown), a tap-gather, a dial, a
 * flip, a zip or a press-and-hold. Replayable; auto-demos the pattern once,
 * and a tap replays it.
 *
 * ── Divergence (vs. the 140+ prior concepts, incl. the security cluster) ────
 * Layout      : a full-bleed constellation of photos with a live status readout
 *               above and a luminous single-secret pricing card below — not a
 *               hero-on-top / feature-bullets / CTA-below stack, and not the
 *               two-object pricing of Strongbox.
 * Interaction : TRACE-YOUR-SECRET — a connect-the-dots path drawn through the
 *               photos. New gesture for the set.
 * Metaphor    : a personal PATTERN / secret shape — the one thing only you
 *               carry in your head. Photos as the nodes keep it unmistakably a
 *               photo vault.
 * Value frame : COMMITMENT + endowment — the secret is one you set yourself,
 *               and it is the only key. Not loss, urgency or fear.
 * Register    : intimate, quietly luminous — night-glass and a warm thread of
 *               light, like tracing a constellation. Calm and premium.
 * CTA         : "Lock it with my secret" — yearly featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (Pattern Lock +
 * Face ID / Touch ID · encrypted Cloud Vault · break-in reports · app
 * disguise). No countdown, no fabricated scarcity, NO data-loss threat —
 * nothing is deleted and nothing leaves the phone; the secret only decides who
 * is allowed to open your photos. "Save 79%" is exact arithmetic from live
 * product data. Yearly is the featured, default, most prominent price; monthly
 * is small and secondary. The 7-day free trial is the real trial. Renders on
 * built-in debug product data with bundled photos — no native injection, no
 * external egress.
 */

// The memories that become the nodes — bundled, self-contained repo photos.
const MEMORIES = [
  "/byheart/m02.jpg",
  "/byheart/m05.jpg",
  "/byheart/m09.jpg",
  "/byheart/m11.jpg",
  "/byheart/m14.jpg",
  "/byheart/m03.jpg",
  "/byheart/m17.jpg",
  "/byheart/m08.jpg",
  "/byheart/m12.jpg",
]

// The canonical secret the auto-demo (and a tap) draws — a symmetric ribbon
// across the nine photos. Any traced shape of 4+ nodes seals; this is just the
// one shown for you.
const SECRET: number[] = [0, 2, 4, 6, 8, 7]
const REQUIRED = 4 // nodes needed for a valid secret

// What every locked vault includes — real Private Photo Vault safeguards.
const INCLUDED = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Your secret + Face ID",
    line: "Pattern, Face ID or Touch ID — only you",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "A private backup, yours on every device",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in reports",
    line: "A silent photo of a wrong attempt",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "Hide the vault behind an ordinary app",
  },
]

// Node centres inside a 300×300 viewBox (3×3 grid).
const CELL = 100
const nodeXY = (i: number) => ({
  x: (i % 3) * CELL + CELL / 2,
  y: Math.floor(i / 3) * CELL + CELL / 2,
})

const TracePaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()
  const darkMode = state.initialData?.darkMode ?? false

  // Blend the safe-area insets into the night-glass so it runs edge to edge.
  useEffect(() => {
    const top = darkMode ? "#0d0f16" : "#eef1f8"
    const bottom = darkMode ? "#090a10" : "#e7ebf5"
    actions.setHeaderInsetColor(top)
    actions.setFooterInsetColor(bottom)
  }, [actions, darkMode])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── secret / seal state ───────────────────────────────────────────────────
  // path : the ordered nodes traced so far.
  // s    : 0 = open (no secret), 1 = sealed (locked with the secret). A spring
  //        chases a target once a valid secret is completed.
  const [path, setPath] = useState<number[]>([])
  const [s, setS] = useState(0)
  const sRef = useRef(0)
  const targetRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)
  const demoTimers = useRef<number[]>([])

  const sealed = s > 0.6

  const tick = useCallback(() => {
    const target = targetRef.current
    const cur = sRef.current
    const k = target > cur ? 0.13 : 0.16
    let next = cur + (target - cur) * k
    if (Math.abs(target - next) < 0.0012) next = target
    sRef.current = next
    setS(next)
    if (next !== target) raf.current = requestAnimationFrame(tick)
    else raf.current = null
  }, [])

  const springTo = useCallback(
    (v: number) => {
      targetRef.current = v
      if (reduceMotion) {
        if (raf.current != null) cancelAnimationFrame(raf.current)
        raf.current = null
        sRef.current = v
        setS(v)
        return
      }
      if (raf.current == null) raf.current = requestAnimationFrame(tick)
    },
    [reduceMotion, tick],
  )

  const clearDemo = useCallback(() => {
    demoTimers.current.forEach((t) => window.clearTimeout(t))
    demoTimers.current = []
  }, [])

  const reset = useCallback(() => {
    clearDemo()
    setPath([])
    springTo(0)
  }, [clearDemo, springTo])

  // Draw the canonical secret on a timer (auto-demo + tap-to-replay).
  const playSecret = useCallback(() => {
    clearDemo()
    setPath([])
    springTo(0)
    if (reduceMotion) {
      setPath(SECRET)
      springTo(1)
      return
    }
    SECRET.forEach((_, i) => {
      const t = window.setTimeout(
        () => setPath(SECRET.slice(0, i + 1)),
        180 * (i + 1),
      )
      demoTimers.current.push(t)
    })
    const done = window.setTimeout(
      () => springTo(1),
      180 * (SECRET.length + 1) + 120,
    )
    demoTimers.current.push(done)
  }, [clearDemo, reduceMotion, springTo])

  // ── pointer tracing ───────────────────────────────────────────────────────
  const padRef = useRef<HTMLDivElement | null>(null)
  const dragging = useRef(false)
  const pathRef = useRef<number[]>([])
  pathRef.current = path

  const nodeAt = useCallback((clientX: number, clientY: number) => {
    const el = padRef.current
    if (!el) return -1
    const r = el.getBoundingClientRect()
    const x = ((clientX - r.left) / r.width) * 300
    const y = ((clientY - r.top) / r.height) * 300
    for (let i = 0; i < 9; i++) {
      const { x: nx, y: ny } = nodeXY(i)
      if ((x - nx) ** 2 + (y - ny) ** 2 <= 40 * 40) return i
    }
    return -1
  }, [])

  const addNode = useCallback((i: number) => {
    if (i < 0) return
    const cur = pathRef.current
    if (cur.includes(i)) return
    setPath([...cur, i])
  }, [])

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      userActed.current = true
      clearDemo()
      dragging.current = true
      // start a fresh trace
      if (raf.current != null) {
        cancelAnimationFrame(raf.current)
        raf.current = null
      }
      sRef.current = 0
      targetRef.current = 0
      setS(0)
      const i = nodeAt(e.clientX, e.clientY)
      setPath(i >= 0 ? [i] : [])
      try {
        ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
      } catch {}
    },
    [clearDemo, nodeAt],
  )

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      addNode(nodeAt(e.clientX, e.clientY))
    },
    [addNode, nodeAt],
  )

  const onUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    const len = pathRef.current.length
    if (len >= REQUIRED) {
      springTo(1) // valid secret → seal
    } else if (len <= 1) {
      playSecret() // a tap → show them how
    } else {
      reset() // too short → let them try again
    }
  }, [playSecret, reset, springTo])

  // Auto-demo once so the payoff reads; reduced-motion arrives sealed & calm.
  useEffect(() => {
    if (reduceMotion) {
      setPath(SECRET)
      targetRef.current = 1
      sRef.current = 1
      setS(1)
      return
    }
    if (window.location.search.includes("nodemo")) return
    const t = window.setTimeout(() => {
      if (!userActed.current) playSecret()
    }, 950)
    return () => {
      window.clearTimeout(t)
      clearDemo()
      if (raf.current != null) cancelAnimationFrame(raf.current)
      raf.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const points = path.map((i) => nodeXY(i))
  const head = points[points.length - 1]

  return (
    <div
      className={`${styles.root} ${sealed ? styles.isSealed : ""}`}
      style={{ ["--s" as any]: s }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <rect
                  x="3.5"
                  y="4.5"
                  width="17"
                  height="15"
                  rx="2.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="3.1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M12 12v2.4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Set up Private Photo Vault
          </span>
          <h1 className={styles.title}>
            {sealed ? (
              <>
                Now they open for
                <br />
                <span className={styles.accent}>your secret only.</span>
              </>
            ) : (
              <>
                Your photos open for
                <br />
                <span className={styles.accentWarn}>anyone.</span>
              </>
            )}
          </h1>
          <p className={styles.sub}>
            {sealed
              ? "Every memory now sits behind a pattern only you know — your secret and Face ID are the only way back in."
              : "Pick up any phone and the memories are simply there. Give yours a secret — trace it once, and it becomes the only way in."}
          </p>
        </section>

        {/* ── the trace pad (hero) ───────────────────────────────────────── */}
        <section className={styles.stage}>
          <div
            className={`${styles.readout} ${sealed ? styles.readoutSealed : ""}`}
            aria-live="polite"
          >
            <span className={styles.readoutDot} aria-hidden="true" />
            {sealed ? (
              <>
                Locked · <b>opens with your secret</b>
              </>
            ) : (
              <>
                Open to <b>anyone holding your phone</b>
              </>
            )}
          </div>

          <div
            ref={padRef}
            className={styles.pad}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            role="button"
            tabIndex={0}
            aria-label={
              sealed
                ? "Locked with your secret. Activate to replay the pattern."
                : "Trace your secret across your photos to lock the vault."
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                userActed.current = true
                sealed ? reset() : playSecret()
              }
            }}
          >
            {/* the nine memories */}
            <div className={styles.grid} aria-hidden="true">
              {MEMORIES.map((src, i) => {
                const on = path.includes(i)
                return (
                  <span
                    key={src}
                    className={`${styles.tile} ${on ? styles.tileOn : ""}`}
                    style={{ backgroundImage: `url(${src})` }}
                  />
                )
              })}
            </div>

            {/* frosted privacy pane that thickens as it seals */}
            <span className={styles.frost} aria-hidden="true" />

            {/* the luminous thread + nodes */}
            <svg
              className={styles.thread}
              viewBox="0 0 300 300"
              aria-hidden="true"
            >
              <defs>
                <filter
                  id="traceGlow"
                  x="-40%"
                  y="-40%"
                  width="180%"
                  height="180%"
                >
                  <feGaussianBlur stdDeviation="3.4" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {points.length >= 2 && (
                <polyline
                  className={styles.line}
                  points={points.map((p) => `${p.x},${p.y}`).join(" ")}
                  filter="url(#traceGlow)"
                />
              )}

              {Array.from({ length: 9 }).map((_, i) => {
                const { x, y } = nodeXY(i)
                const on = path.includes(i)
                return (
                  <g key={i}>
                    <circle
                      className={styles.nodeHalo}
                      cx={x}
                      cy={y}
                      r={on ? 15 : 9}
                      opacity={on ? 0.55 : 0}
                    />
                    <circle
                      className={`${styles.node} ${on ? styles.nodeOn : ""}`}
                      cx={x}
                      cy={y}
                      r={on ? 7 : 4.5}
                    />
                  </g>
                )
              })}

              {head && !sealed && (
                <circle
                  className={styles.headDot}
                  cx={head.x}
                  cy={head.y}
                  r="5.5"
                />
              )}
            </svg>

            {/* the lock that draws shut, dead centre, once sealed */}
            <span
              className={`${styles.lock} ${sealed ? styles.lockShut : ""}`}
              aria-hidden="true"
            >
              <span className={styles.lockPuck}>
                <svg viewBox="0 0 24 24" width="26" height="26">
                  <path
                    className={styles.shackle}
                    d="M8 10V7.5a4 4 0 0 1 8 0V10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <rect
                    x="5.5"
                    y="10"
                    width="13"
                    height="10"
                    rx="2.4"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <span className={styles.faceSeal}>Face ID · only you</span>
            </span>

            {/* hint chip */}
            <span
              className={`${styles.hint} ${sealed ? styles.hintDone : ""}`}
              aria-hidden="true"
            >
              {sealed ? "Locked with your secret" : "Trace your secret"}
            </span>
          </div>
        </section>

        {/* ── honest framing ─────────────────────────────────────────────── */}
        <p className={styles.proof}>
          <span className={styles.proofMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <circle
                cx="12"
                cy="12"
                r="8.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path
                d="M8.5 12.4l2.4 2.4 4.6-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing leaves your phone and nothing is deleted — your secret only
          decides who is allowed to open your photos.
        </p>

        {/* ── what the locked vault includes (real safeguards) ──────────── */}
        <div className={styles.included}>
          <div className={styles.includedHead}>
            <span>What your locked vault includes</span>
            <span className={styles.includedCount}>
              {INCLUDED.length} safeguards
            </span>
          </div>
          <div className={styles.includedGrid}>
            {INCLUDED.map((it) => (
              <div key={it.key} className={styles.incl}>
                <span className={styles.inclIcon} aria-hidden="true">
                  <it.Icon width={17} height={17} />
                </span>
                <span className={styles.inclName}>{it.name}</span>
                <span className={styles.inclLine}>{it.line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plansLabel}>Keep every memory locked</div>

        <button
          className={`${styles.yearly} ${
            plan === "yearly" ? styles.selected : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyThread} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>
              Full year of Premium
              <span className={styles.trialPill}>
                {trialDays}-day free trial
              </span>
            </span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyFoot}>
            Every safeguard, on for a full year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${
            plan === "monthly" ? styles.monthlySel : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span>Or pay monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Lock it with my secret — ${trialDays}-day free trial`
              : "Lock it with my secret"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default TracePaywall
